import React from 'react';
import { formatNumber } from './utils';

// Currency symbols
const CURRENCY_SYMBOLS = {
  USD: '$',
  EUR: '€',
  MXN: 'MX$',
  PEN: 'S/',
  COP: 'COL$',
  GBP: '£',
  CAD: 'CA$',
  BRL: 'R$',
  ARS: 'AR$',
};

/**
 * Display currency amounts in "original → USD" format
 *
 * @param {Object} originalAmounts - Object like {EUR: 9200, USD: 500}
 * @param {Number} usdTotal - Converted USD total
 * @param {Boolean} compact - If true, use compact format
 */
export const CurrencyDisplay = ({ originalAmounts, usdTotal, compact = false }) => {
  if (!originalAmounts || Object.keys(originalAmounts).length === 0) {
    // No original amounts, just show USD
    return <span>${formatNumber(usdTotal)} USD</span>;
  }

  // Filter out zero amounts
  const nonZeroAmounts = Object.entries(originalAmounts)
    .filter(([_, amount]) => amount > 0)
    .sort(([a], [b]) => a.localeCompare(b)); // Sort alphabetically

  if (nonZeroAmounts.length === 0) {
    return <span>${formatNumber(usdTotal)} USD</span>;
  }

  // Format each currency amount
  const formattedAmounts = nonZeroAmounts.map(([currency, amount]) => {
    const symbol = CURRENCY_SYMBOLS[currency] || currency;
    return `${symbol}${formatNumber(amount)}`;
  });

  if (compact && formattedAmounts.length > 2) {
    // Compact: show first currency + "..."
    return (
      <span className="currency-display">
        <span className="original-currency">{formattedAmounts[0]}</span>
        {formattedAmounts.length > 1 && (
          <span className="currency-more"> +{formattedAmounts.length - 1} más</span>
        )}
        <span className="arrow"> → </span>
        <span className="usd-amount">${formatNumber(usdTotal)} USD</span>
      </span>
    );
  }

  // Full display
  return (
    <span className="currency-display">
      <span className="original-currency">
        {formattedAmounts.join(' + ')}
      </span>
      <span className="arrow"> → </span>
      <span className="usd-amount">${formatNumber(usdTotal)} USD</span>
    </span>
  );
};

/**
 * Simple helper to format a single currency amount
 */
export const formatCurrency = (amount, currency = 'USD') => {
  const symbol = CURRENCY_SYMBOLS[currency] || currency;
  return `${symbol}${formatNumber(amount)}`;
};

export default CurrencyDisplay;
